import { NewsItem } from "@/types/newsType";
import { api } from "../core/api.client";
import { API_ENDPOINTS } from "../core/api.config";
import {
  ApiClientResponse,
  QueryParams,
  RequestOptions,
  PaginationMeta,
} from "../core/api.types";

export interface NewsListResponse {
  docs: NewsItem[];
  meta: PaginationMeta;
}

export interface NewsQueryParams extends QueryParams {
  category?: string;
  featured?: boolean;
  published?: boolean;
  author?: string;
}

/**
 * News API class
 */
export class NewsApi {
  private endpoint = API_ENDPOINTS.COLLECTIONS.NEWS;

  /**
   * Get all news with optional filtering
   */
  async list(
    params: NewsQueryParams = {},
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return api.get<NewsItem[]>(this.endpoint, {
      ...options,
      params: {
        limit: 20,
        sort: "-publishedDate",
        ...params,
        where: {
          published: { equals: true },
          ...params.where,
        },
      },
    });
  }

  /**
   * Get featured news for carousel
   */
  async getFeatured(
    limit: number = 5,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.list(
      {
        featured: true,
        limit,
        sort: "-publishedDate",
      },
      options
    );
  }

  /**
   * Get news by category
   */
  async getByCategory(
    category: string,
    params: NewsQueryParams = {},
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.list(
      {
        ...params,
        where: {
          category: { equals: category },
          ...params.where,
        },
      },
      options
    );
  }

  /**
   * Get internal news (Tin nội bộ)
   */
  async getInternal(
    params: NewsQueryParams = {},
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.getByCategory("Tin nội bộ", params, options);
  }

  /**
   * Get general news (Tin tức chung)
   */
  async getGeneral(
    params: NewsQueryParams = {},
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.getByCategory("Tin tức chung", params, options);
  }

  /**
   * Get recent news
   */
  async getRecent(
    limit: number = 10,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.list(
      {
        limit,
        sort: "-publishedDate",
      },
      options
    );
  }

  /**
   * Get single news by ID
   */
  async getById(
    id: string,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem>> {
    return api.get<NewsItem>(`${this.endpoint}/${id}`, options);
  }

  /**
   * Get single news by slug
   */
  async getBySlug(
    slug: string,
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return api.get<NewsItem[]>(this.endpoint, {
      ...options,
      params: {
        where: {
          slug: { equals: slug },
          published: { equals: true },
        },
        limit: 1,
      },
    });
  }

  /**
   * Search news by title or content
   */
  async search(
    query: string,
    params: NewsQueryParams = {},
    options: RequestOptions = {}
  ): Promise<ApiClientResponse<NewsItem[]>> {
    return this.list(
      {
        ...params,
        where: {
          or: [
            { title: { contains: query } },
            { excerpt: { contains: query } },
            { content: { contains: query } },
          ],
          ...params.where,
        },
      },
      options
    );
  }
}

// Export singleton instance
export const newsApi = new NewsApi();

export default newsApi;
